import { PlusIcon } from "@heroicons/react/24/solid";
import React, { useState } from "react";
import { Toaster } from "react-hot-toast";
import ReactTooltip from "react-tooltip";
import { useParams } from "react-router-dom";
import Breadcrumbs from "../../components/Breadcrumbs";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { usePermissions } from "../../context/PermissionsContext";
import { createChecklistItem } from "../../services/createChecklistItem";
import { updateChecklistItem } from "../../services/updateChecklistItem";
import CreateNewChecklistItemFormSection from "../TaskForm/CreateNewChecklistFormSection";
import AddNewItemToJobModal from "./AddNewItemToJobModal";
import toast from "react-hot-toast";
import { addServiceJobWithChecklistItems } from "../../services/serviceTasks/addServiceJobWithChecklistItems";
import { term } from "../../terms";

function CreateJobV2() {
    const { can } = usePermissions();
    const params = useParams();

    const [job, setJob] = useState({ name: "", checklistItems: [] });
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [inputCheckList, setInputCheckList] = useState([]);
    const [openAddItem, setOpenAddItem] = useState(false);
    const canWritePipelines = can("pipelines.write");
    const noPermissionMessage = "You don't have permission to do this.";

    const removeChecklistItem = (checklistItemId) => {
        const newChecklist = job.checklistItems.filter(
            (item) => item.id !== checklistItemId,
        );
        setJob({ ...job, checklistItems: newChecklist });
    };

    const InitializeForm = () => {
        setJob({ name: "", checklistItems: [] });
        setInputCheckList([]);
    };

    const onSave = async () => {
        if (!canWritePipelines) {
            toast.error(noPermissionMessage);
            return;
        }

        if (job.name === "") {
            toast.error("Name is required.");
            return;
        }
        if (job.checklistItems.length === 0) {
            toast.error("At least one checklist item is required.");
            return;
        }

        const jobWithLinksInJson = { ...job };

        const checklistItemsWithLinksInString = job.checklistItems.map(
            (checklistItem) => {
                return {
                    ...checklistItem,
                    links: JSON.stringify(checklistItem.links),
                };
            },
        );

        const jobToAdd = { ...job, checklistItems: checklistItemsWithLinksInString };

        const response = await addServiceJobWithChecklistItems(
            params.jobId,
            jobToAdd,
        );
        if (response.success) {
            toast.success("Step created successfully.");
            InitializeForm();
        } else {
            toast.error("Failed to create step.");
            setJob(jobWithLinksInJson);
        }
    };

    const onClickAddItem = () => {
        if (!canWritePipelines) return;
        setOpenAddItem(true);
    };

    return (
        <section>
            <AddNewItemToJobModal
                open={openAddItem}
                setOpen={setOpenAddItem}
                createChecklistItem={createChecklistItem}
                job={job}
                setJob={setJob}
            />
            <Toaster />
            <ReactTooltip
                type="light"
                effect="solid"
                place="top"
                border={true}
                borderColor={"rgb(220,220,220)"}
                className="rounded-md bg-gray-50"
                multiline={false}
            />
            <div className="flex h-screen overflow-hidden">
                {/* Sidebar */}
                <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
                {/* Content area */}
                <div className="relative flex flex-col flex-1 overflow-x-hidden overflow-y-auto">
                    {/*  Site header */}
                    <Header
                        sidebarOpen={sidebarOpen}
                        setSidebarOpen={setSidebarOpen}
                    />

                    <main>
                        <div className="w-full px-4 py-8 mx-auto sm:px-6 lg:px-8 max-w-9xl">
                            <div className="flex flex-col items-start max-w-2xl">
                                <Breadcrumbs
                                    home="/templates/jobs"
                                    pages={[
                                        {
                                            name: `${term("pipeline_singular")}`,
                                            href: `/templates/job/${params.jobId}`,
                                            current: true,
                                        },
                                        {
                                            name: `Create ${term("step_singular")}`,
                                            href: `/templates/job-templates/${params.jobId}/jobs/create`,
                                            current: true,
                                        },
                                    ]}
                                />

                                <h1 className="p-4 text-2xl font-bold md:text-3xl text-slate-800">
                                    Create {term("step_singular")}
                                </h1>
                                <div className="relative w-full pb-4 bg-white border rounded-sm shadow-lg border-slate-200">
                                    <div className="px-5 py-4 space-y-5">
                                        <div>
                                            <label
                                                htmlFor="email"
                                                className="block text-sm font-medium text-gray-700"
                                            >
                                                Name
                                            </label>
                                            <div className="mt-1">
                                                <input
                                                    type="text"
                                                    onChange={(e) =>
                                                        setJob({
                                                            ...job,
                                                            name: e.target.value,
                                                        })
                                                    }
                                                    value={job.name}
                                                    required
                                                    disabled={!canWritePipelines}
                                                    className={`w-full shadow-sm border border-slate-300 rounded-sm p-2`}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="relative w-full mt-12 bg-white border rounded-sm shadow-lg border-slate-200">
                                    <header className="flex items-center justify-between w-full px-5 py-4 border-b border-slate-200">
                                        <h2 className="font-semibold text-slate-800">
                                            Checklist Items
                                        </h2>
                                        <span
                                            data-tip={
                                                canWritePipelines
                                                    ? "Add item"
                                                    : noPermissionMessage
                                            }
                                            className="inline-flex"
                                        >
                                            <button
                                                id="add-btn"
                                                onClick={() => onClickAddItem()}
                                                type="button"
                                                disabled={!canWritePipelines}
                                                className={`inline-flex items-center p-1 text-white bg-teal-500 border border-transparent rounded-sm shadow-sm ${
                                                    canWritePipelines
                                                        ? "hover:bg-teal-600"
                                                        : "opacity-50 cursor-not-allowed"
                                                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500`}
                                            >
                                                <PlusIcon className="w-5 h-5" />
                                            </button>
                                        </span>
                                    </header>

                                    <CreateNewChecklistItemFormSection
                                        inputCheckList={inputCheckList}
                                        setInputCheckList={setInputCheckList}
                                        updateChecklistItem={updateChecklistItem}
                                        removeChecklistItem={removeChecklistItem}
                                        createChecklistItem={createChecklistItem}
                                        job={job}
                                        setJob={setJob}
                                    />
                                </div>
                                <div className="flex justify-end w-full">
                                    <span
                                        data-tip={
                                            canWritePipelines
                                                ? "Save"
                                                : noPermissionMessage
                                        }
                                        className="inline-flex"
                                    >
                                        <button
                                            onClick={onSave}
                                            disabled={!canWritePipelines}
                                            className={`z-50 inline-flex justify-end px-4 py-2 mt-8 text-sm font-medium text-white bg-teal-500 border border-transparent rounded-sm shadow-sm ${
                                                canWritePipelines
                                                    ? "hover:bg-teal-600"
                                                    : "opacity-50 cursor-not-allowed"
                                            } focus:outline-none focus:ring-0`}
                                        >
                                            Save
                                        </button>
                                    </span>
                                </div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
        </section>
    );
}
export default CreateJobV2;
